import type { ReadingQuestion } from '@examing/types';
import { AxiosPromise } from 'axios';
import type { PublicMarketplaceCategory } from './types';
import { http } from 'services';
import * as Types from './types';

export const List = (params?: Types.IApi.List.Params): AxiosPromise<Types.IApi.List.Response> =>
  http.request.get('/api/tests', { params });

// Tests the current user can take via collections attached to their teams.
export const Available = (params?: Types.IApi.List.Params): AxiosPromise<Types.IApi.List.Response> =>
  http.request.get('/api/tests/available', { params });

// Public mock tests catalog (unauthenticated & student marketplace)
export const PublicTests = (params?: {
  category?: string;
  subject?: string;
  accessType?: string;
  /** Marketplace level (beginner · intermediate · advanced · exam_standard). */
  difficulty?: string;
  search?: string;
  page?: number;
  perPage?: number;
}): AxiosPromise<{
  data: Array<{
    id: string;
    title: string;
    description?: string;
    category?: string;
    accessType: 'FREE' | 'PAID' | 'PLAN';
    priceSom: number;
    durationMinutes: number;
    questionsCount: number;
    sectionsCount: number;
    tags?: string[];
    difficultyLevel?: string;
    coverImageUrl?: string;
    viewsCount?: { total: number; landing: number; portal: number };
    createdAt?: string;
  }>;
  meta: { total: number; page: number; perPage: number; totalPages: number };
}> => http.request.get('/api/public/tests', { params });

export const PublicCategories = (): AxiosPromise<{
  data: PublicMarketplaceCategory[];
}> => http.request.get('/api/public/tests/categories');

// The list's filters without a subject; one row per subject of the public catalog.
export const PublicSubjects = (params?: {
  category?: string;
  accessType?: string;
  difficulty?: string;
}): AxiosPromise<{
  data: Types.PublicSubjectCard[];
  meta: { total: number };
}> => http.request.get('/api/public/tests/subjects', { params });

// The learner's own best result, goal and weak topics in each subject of one category.
export const MySubjectProgress = (category: string, signal?: AbortSignal): AxiosPromise<{
  data: Types.SubjectProgress[];
}> => http.request.get('/api/public/tests/subject-progress', { params: { category }, signal });

// Public tests the learner has begun or already paid for, by test id.
export const MyHoldings = (): AxiosPromise<{
  inProgressTestIds: string[];
  purchasedTestIds: string[];
}> => http.request.get('/api/public/tests/mine');

export const PurchasePublicTest = (id: string, useSubscription = false): AxiosPromise<{
  ok: boolean;
  purchaseId?: string;
  balanceSom: number;
}> => http.request.post(`/api/public/tests/${id}/purchase`, { useSubscription });

// Teams that can access this test through the collections it belongs to.
export const TeamAccess = (id: string): AxiosPromise<{ data: Array<{ id: string; name: string }> }> =>
  http.request.get(`/api/tests/${id}/team-access`);

export const Single = ({ id }: Types.IApi.Single.Params): AxiosPromise<Types.IApi.Single.Response> =>
  http.request.get(`/api/tests/${id}`);

export const Create = (data: Types.IApi.Create.Request): AxiosPromise<Types.IApi.Create.Response> =>
  http.request.post('/api/tests', data);

export const Update = ({ id, ...data }: Types.IApi.Update.Request): AxiosPromise<Types.IApi.Update.Response> =>
  http.request.patch(`/api/tests/${id}`, data);

export const Delete = ({ id }: Types.IApi.Delete.Params): AxiosPromise<Types.IApi.Delete.Response> =>
  http.request.delete(`/api/tests/${id}`);

// POST /publish persists the publish metadata (grade, language, coverImage,
// teachingGoal, visibility) AND activates the test in one call.
export const Publish = (id: string, data?: Record<string, unknown>): AxiosPromise =>
  http.request.post(`/api/tests/${id}/publish`, data ?? {});

export const Archive = (id: string): AxiosPromise =>
  http.request.post(`/api/tests/${id}/archive`);

export const Duplicate = (id: string, data: { title?: string } = {}): AxiosPromise<{ id: string }> =>
  http.request.post(`/api/tests/${id}/duplicate`, data);

// Section APIs
export const AddSection = (
  testId: string,
  data: Types.IApi.AddSection.Request
): AxiosPromise<Types.IApi.AddSection.Response> =>
  http.request.post(`/api/tests/${testId}/sections`, data);

// Question APIs
export const AddQuestion = (
  sectionId: string,
  data: Types.IApi.AddQuestion.Request
): AxiosPromise<Types.IApi.AddQuestion.Response> =>
  http.request.post(`/api/tests/sections/${sectionId}/questions`, data);

export const UpdateQuestion = (
  questionId: string,
  data: Partial<Types.IApi.AddQuestion.Request>
): AxiosPromise<Types.IApi.UpdateQuestion.Response> =>
  http.request.patch(`/api/tests/questions/${questionId}`, data);

export const DeleteQuestion = (questionId: string): AxiosPromise<void> =>
  http.request.delete(`/api/tests/questions/${questionId}`);

export const BulkDeleteQuestions = (
  questionIds: string[]
): AxiosPromise<{ deleted: number }> =>
  http.request.post('/api/tests/questions/bulk-delete', { questionIds });

export const CopyQuestions = (
  targetTestId: string,
  questionIds: string[]
): AxiosPromise<{ copied: number; message: string }> =>
  http.request.post(`/api/tests/${targetTestId}/questions/copy`, { questionIds });

// Config APIs
export const UpdateConfigBasic = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/basic`, data);

export const UpdateConfigAccess = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/access`, data);

// No-account public candidate link controls (enable/disable + activation window
// + result visibility + attempts cap), and token rotation.
export const UpdatePublicLink = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/public-link`, data);

export const RegeneratePublicLink = (id: string): AxiosPromise<unknown> =>
  http.request.post(`/api/tests/${id}/public-link/regenerate`);

export const UpdateConfigGrading = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/grading`, data);

export const UpdateConfigTime = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/time`, data);

export const UpdateConfigCertificate = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/certificate`, data);

export const Activate = (id: string): AxiosPromise<unknown> =>
  http.request.post(`/api/tests/${id}/activate`);

export const UpdateConfigAntiCheating = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/anti-cheating`, data);

export const UpdateConfigGamification = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/gamification`, data);

export const UpdateConfigWorksheet = (id: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/tests/${id}/config/worksheet`, data);

// AI Question Generation APIs
export interface GenerateQuestionsByTopicParams {
  topic: string;
  context?: string;
  questionCount: number;
  questionTypes: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
  language?: string;
  subjectId?: string;
  /** Generation field key for domain-specific steering. */
  fieldKey?: string;
  /** Author-edited steering instructions; overrides server-side fieldKey resolution. */
  fieldPrompt?: string;
}

export interface AIGeneratedQuestion {
  type: string;
  prompt: string;
  options?: Array<{ id: string; value: string }>;
  matchPairs?: Array<{ key: string; value: string }>;
  blanks?: Array<{ id: string; position: number; acceptedAnswers: string[] }>;
  reorderItems?: Array<{ id: string; content: string }>;
  correctOrder?: string[];
  // PASSAGE
  matchingOptions?: string[];
  answerMode?: 'exact' | 'manual';
  acceptedAnswers?: string[];
  maxWords?: number | null;
  passageText?: string;
  passageTitle?: string;
  subQuestions?: ReadingQuestion[];
  correctAnswer?: string | string[] | Record<string, string>;
  explanation?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
}

export interface GenerateQuestionsResponse {
  success: boolean;
  data: {
    questions: AIGeneratedQuestion[];
    count: number;
    topic: string;
  };
}

export const GenerateQuestionsByTopic = (
  params: GenerateQuestionsByTopicParams
): AxiosPromise<GenerateQuestionsResponse> =>
  http.request.post('/api/questions/generate-by-topic', params);

export interface GenerateSimilarQuestionsParams {
  questionId: string;
  count: number;
  preserveDifficulty?: boolean;
  preserveType?: boolean;
}

export interface GenerateSimilarResponse {
  success: boolean;
  data: {
    questions: AIGeneratedQuestion[];
    count: number;
    originalQuestionId: string;
  };
}

export const GenerateSimilarQuestions = (
  params: GenerateSimilarQuestionsParams
): AxiosPromise<GenerateSimilarResponse> =>
  http.request.post('/api/questions/generate-similar', params);

export const Finish = (id: string): AxiosPromise<unknown> =>
  http.request.post(`/api/tests/${id}/finish`);

export interface SubmitMarketplacePayload {
  category: string;
  subjectKey?: string;
  variantNumber?: number;
  isLeadVariant?: boolean;
  difficultyLevel: string;
  description: string;
  tags: string[];
  coverImage?: string;
  accessType?: 'FREE' | 'PAID' | 'PLAN';
  priceSom?: number;
}

export const SubmitMarketplace = (id: string, data: SubmitMarketplacePayload): AxiosPromise =>
  http.request.post(`/api/tests/${id}/marketplace/submit`, data);

export const CancelMarketplace = (id: string): AxiosPromise =>
  http.request.post(`/api/tests/${id}/marketplace/cancel`);


export const PublicTestSummary = (id: string): AxiosPromise<{ publicLinkToken?: string; accessType: "FREE" | "PAID" | "PLAN" }> =>
  http.request.get(`/api/public/tests/${encodeURIComponent(id)}`);

export const RecordTestView = (
  id: string,
  source: 'LANDING' | 'PORTAL' = 'PORTAL',
): AxiosPromise<{ recorded: boolean; cooldown?: boolean; viewsCount?: { total: number; landing: number; portal: number } }> =>
  http.request.post(`/api/public/tests/${encodeURIComponent(id)}/view`, { source });
